import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { DrawerCloseEvent, DrawerOpenEvent } from '@theme/theme-drawer';

/** The localStorage key that holds the saved product ids, newest first. */
const STORAGE_KEY = 'wishlistProducts';

/**
 * Side record of each saved product's handle, `{ [id]: handle }`. Search leaves out products hidden
 * from it (Unlisted, or `seo.hidden`), so the drawer loads those from their product URL instead.
 */
const HANDLES_KEY = `${STORAGE_KEY}:handles`;

/** Side record of saved products the drawer could not load, `{ [id]: firstMissedAt }` (ms). */
const MISSING_KEY = `${STORAGE_KEY}:missing`;

/**
 * How long a saved product can keep failing to load before the drawer drops it (30 days). One miss
 * doesn't prove a product is gone: it can be hidden from search, not sold in the current market or
 * not indexed yet. A product that loads again starts over.
 */
const MISS_GRACE_MS = 30 * 24 * 60 * 60 * 1000;

/** The most products a wishlist keeps; saving another one drops the oldest. */
const MAX_PRODUCTS = 50;

/** Dispatched on `document` whenever the saved ids change. */
const CHANGE_EVENT = 'wishlist:change';

/** The `<theme-drawer>` that lists the saved products (`snippets/wishlist-drawer.liquid`). */
const DRAWER_ID = 'wishlist-drawer';

/** Links to this hash open the drawer instead of navigating, so merchants can add `/#wishlist` to a menu. */
const DRAWER_HASH = '#wishlist';

/**
 * The section file rendered through the Section Rendering API to build the drawer rows. Rendering a
 * section by file name gives it this id.
 */
const RESULTS_SECTION_ID = 'wishlist-results';

/** Ids per search request; `sections/wishlist-results.liquid` renders up to this many products. */
const REQUEST_BATCH_SIZE = 12;

/** Placeholder rows shown while the first rows load. */
const MAX_SKELETON_ROWS = 4;

/** Live regions announce text that changes after they are in the accessibility tree. */
const ANNOUNCE_DELAY = 100;

/** The longest wait for the header menu to finish closing before the drawer opens anyway. */
const MENU_CLOSE_TIMEOUT = 1000;

/**
 * @typedef {object} WishlistChangeDetail
 * @property {string[]} ids - The saved product ids after the change, newest first.
 * @property {'add' | 'remove' | 'cleanup' | 'sync'} action - What changed the list: a shopper on this
 *   page, the drawer dropping products that have failed to load for {@link MISS_GRACE_MS}, or another
 *   tab / a restored page.
 * @property {string[]} productIds - The products that were added or removed (empty for `sync`).
 */

/**
 * @param {unknown} value
 * @returns {value is string} Whether the value is a numeric product id.
 */
function isProductId(value) {
  return typeof value === 'string' && /^\d{1,20}$/.test(value);
}

/**
 * @param {unknown} value
 * @returns {value is string} Whether the value can be a product handle in a `/products/<handle>` path.
 */
function isProductHandle(value) {
  return typeof value === 'string' && value.length > 0 && value.length <= 255 && !/[\s/?#\\]/.test(value);
}

/**
 * @param {object} object
 * @param {string} key
 * @returns {boolean} Whether the object has the key as its own property.
 */
function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

/**
 * Reads a JSON object kept under a localStorage key.
 * @param {string} key
 * @returns {Record<string, unknown> | null} The stored object (`{}` when nothing valid is stored), or
 *   `null` when storage can't be read.
 */
function readRecord(key) {
  /** @type {string | null} */
  let raw = null;

  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;
  }

  try {
    const value = JSON.parse(raw || '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
}

/**
 * Writes a JSON object under a localStorage key; an empty object removes the key.
 * @param {string} key
 * @param {Record<string, unknown>} record
 * @returns {boolean} Whether the record was saved.
 */
function writeRecord(key, record) {
  try {
    if (Object.keys(record).length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(record));
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Keeps the shopper's saved product ids in localStorage, newest first, at most {@link MAX_PRODUCTS}.
 *
 * Storage can be unavailable (privacy modes, blocked site data) or hold unexpected values, so every
 * access is guarded: reads fall back to an empty list and writes report whether they were saved.
 * Every saved change dispatches {@link CHANGE_EVENT} on `document`.
 */
export class Wishlist {
  /** @type {boolean | null} */
  static #available = null;

  /** The raw stored value the cached ids were parsed from. @type {string | null | undefined} */
  static #raw = undefined;

  /** @type {string[]} */
  static #ids = [];

  /** @returns {string} The event dispatched on `document` after the saved ids change. */
  static get CHANGE_EVENT() {
    return CHANGE_EVENT;
  }

  /** @returns {number} The most products a wishlist keeps. */
  static get maxProducts() {
    return MAX_PRODUCTS;
  }

  /** @returns {boolean} Whether the browser lets the wishlist persist on this device. */
  static get isAvailable() {
    if (Wishlist.#available === null) {
      try {
        const probeKey = `${STORAGE_KEY}:probe`;
        localStorage.setItem(probeKey, '1');
        localStorage.removeItem(probeKey);
        Wishlist.#available = true;
      } catch {
        Wishlist.#available = false;
      }
    }

    return Wishlist.#available;
  }

  /**
   * The saved product ids, newest first. Invalid and duplicate entries are dropped.
   * @returns {string[]}
   */
  static getProducts() {
    /** @type {string | null} */
    let raw = null;

    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch {
      return [];
    }

    if (raw !== Wishlist.#raw) {
      Wishlist.#raw = raw;
      Wishlist.#ids = Wishlist.#parse(raw);
    }

    return [...Wishlist.#ids];
  }

  /**
   * @param {string | number} productId
   * @returns {boolean} Whether the product is saved.
   */
  static has(productId) {
    return Wishlist.getProducts().includes(String(productId));
  }

  /**
   * Saves a product as the newest entry.
   * @param {string | number} productId
   * @param {string} [handle] - The product handle, so the drawer can load the product when search
   *   leaves it out.
   * @returns {boolean} Whether the change was saved.
   */
  static add(productId, handle) {
    const id = String(productId ?? '').trim();
    if (!isProductId(id)) return false;

    const ids = [id, ...Wishlist.getProducts().filter((savedId) => savedId !== id)].slice(0, MAX_PRODUCTS);
    if (isProductHandle(handle)) Wishlist.#updateRecord(HANDLES_KEY, ids, new Map([[id, handle]]));

    return Wishlist.#save(ids, 'add', [id]);
  }

  /**
   * Removes products from the wishlist.
   * @param {Array<string | number>} productIds
   * @param {'remove' | 'cleanup'} [action]
   * @returns {boolean} Whether the change was saved (also true when nothing had to change).
   */
  static remove(productIds, action = 'remove') {
    const removed = new Set(productIds.map((productId) => String(productId)));
    const saved = Wishlist.getProducts();
    const ids = saved.filter((id) => !removed.has(id));

    if (ids.length === saved.length) return true;

    return Wishlist.#save(
      ids,
      action,
      saved.filter((id) => removed.has(id))
    );
  }

  /**
   * Re-reads the stored ids after another tab changed them, or after the page was restored from the
   * back/forward cache, and tells the page.
   */
  static sync() {
    Wishlist.#raw = undefined;
    Wishlist.#notify({ ids: Wishlist.getProducts(), action: 'sync', productIds: [] });
  }

  /**
   * Records the handles of saved products (from toggles and from loaded drawer rows).
   * @param {Map<string, string>} handles - Handles by product id. Invalid entries and products that
   *   aren't saved are skipped.
   */
  static rememberHandles(handles) {
    /** @type {Map<string, string>} */
    const valid = new Map();
    for (const [id, handle] of handles) {
      if (isProductId(id) && isProductHandle(handle)) valid.set(id, handle);
    }

    if (valid.size > 0) Wishlist.#updateRecord(HANDLES_KEY, Wishlist.getProducts(), valid);
  }

  /**
   * @returns {Map<string, string>} The known handles of saved products, by product id.
   */
  static getHandles() {
    const record = readRecord(HANDLES_KEY) ?? {};
    /** @type {Map<string, string>} */
    const handles = new Map();

    for (const id of Wishlist.getProducts()) {
      const handle = hasOwn(record, id) ? record[id] : undefined;
      if (isProductHandle(handle)) handles.set(id, handle);
    }

    return handles;
  }

  /**
   * Records which saved products the drawer could and couldn't load, and returns the ones that have
   * failed to load for longer than {@link MISS_GRACE_MS}; only those may be dropped. A product that
   * loads again starts over. When storage can't be read or written, nothing is returned, so nothing
   * is dropped.
   * @param {string[]} missing - Saved products the drawer couldn't load.
   * @param {string[]} found - Saved products the drawer loaded.
   * @returns {string[]} The missing products to drop.
   */
  static noteMisses(missing, found) {
    const record = readRecord(MISSING_KEY);
    if (!record) return [];

    const saved = new Set(Wishlist.getProducts());
    const now = Date.now();
    /** @type {Record<string, number>} */
    const next = {};

    for (const [id, missedAt] of Object.entries(record)) {
      if (!saved.has(id) || found.includes(id)) continue;
      if (typeof missedAt !== 'number' || !Number.isFinite(missedAt)) continue;

      // A clock set back later than the first miss restarts the wait rather than stretching it.
      next[id] = Math.min(missedAt, now);
    }

    for (const id of missing) {
      if (saved.has(id) && !hasOwn(next, id)) next[id] = now;
    }

    if (!writeRecord(MISSING_KEY, next)) return [];

    return missing.filter((id) => hasOwn(next, id) && now - Number(next[id]) >= MISS_GRACE_MS);
  }

  /**
   * @param {string[]} ids
   * @param {'add' | 'remove' | 'cleanup'} action
   * @param {string[]} productIds
   * @returns {boolean}
   */
  static #save(ids, action, productIds) {
    const raw = JSON.stringify(ids);

    try {
      localStorage.setItem(STORAGE_KEY, raw);
    } catch {
      return false;
    }

    Wishlist.#raw = raw;
    Wishlist.#ids = ids;

    // Side records only describe saved products.
    Wishlist.#updateRecord(HANDLES_KEY, ids);
    Wishlist.#updateRecord(MISSING_KEY, ids);

    Wishlist.#notify({ ids: [...ids], action, productIds });
    return true;
  }

  /**
   * Drops a side record's entries for products that aren't saved, and merges in new values.
   * @param {string} key
   * @param {string[]} ids - The saved product ids.
   * @param {Map<string, unknown>} [updates] - New values by product id (saved products only).
   */
  static #updateRecord(key, ids, updates) {
    const record = readRecord(key);
    if (!record) return;

    const saved = new Set(ids);
    let changed = false;

    for (const id of Object.keys(record)) {
      if (saved.has(id)) continue;

      delete record[id];
      changed = true;
    }

    for (const [id, value] of updates ?? []) {
      if (!saved.has(id) || record[id] === value) continue;

      record[id] = value;
      changed = true;
    }

    if (changed) writeRecord(key, record);
  }

  /** @param {WishlistChangeDetail} detail */
  static #notify(detail) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail }));
  }

  /**
   * @param {string | null} raw
   * @returns {string[]}
   */
  static #parse(raw) {
    /** @type {unknown} */
    let stored;

    try {
      stored = JSON.parse(raw || '[]');
    } catch {
      return [];
    }

    if (!Array.isArray(stored)) return [];

    /** @type {string[]} */
    const ids = [];

    for (const value of stored) {
      const id = String(value);
      if (!isProductId(id) || ids.includes(id)) continue;

      ids.push(id);
      if (ids.length === MAX_PRODUCTS) break;
    }

    return ids;
  }
}

/**
 * Interface strings rendered by `snippets/wishlist-drawer.liquid`.
 * @typedef {object} WishlistStrings
 * @property {string} [added] - Contains `[product]`.
 * @property {string} [removed] - Contains `[product]`.
 * @property {string} [saveError]
 * @property {string} [countOne]
 * @property {string} [countOther] - Contains `{{ count }}`.
 * @property {string} [unavailableOne]
 * @property {string} [unavailableOther] - Contains `{{ count }}`.
 */

/** @type {WishlistStrings | null} */
let strings = null;

/** @returns {WishlistStrings} */
function getStrings() {
  if (strings) return strings;

  const script = document.querySelector('script[data-wishlist-strings]');

  try {
    strings = /** @type {WishlistStrings} */ (JSON.parse(script?.textContent || '{}'));
  } catch {
    strings = {};
  }

  return strings;
}

/**
 * @param {keyof WishlistStrings} key
 * @param {string} product - The product title.
 * @returns {string} The string with the product title filled in, or '' when the string is missing.
 */
function productString(key, product) {
  const template = getStrings()[key];
  // A replacer function keeps `$` sequences in product titles literal.
  return template ? template.replace('[product]', () => product) : '';
}

/**
 * @param {number} count
 * @param {string | undefined} one
 * @param {string | undefined} other - Contains `{{ count }}`.
 * @returns {string} The string for the count, or '' when it is missing.
 */
function pluralString(count, one, other) {
  const template = count === 1 ? one ?? other : other;
  return template ? template.replace('{{ count }}', String(count)) : '';
}

/**
 * @param {number} count
 * @returns {string} The saved item count, for screen readers.
 */
function countString(count) {
  if (count === 0) return '';

  const { countOne, countOther } = getStrings();
  return pluralString(count, countOne, countOther) || String(count);
}

/**
 * Routes every wishlist announcement through one place, so each change is announced once.
 *
 * Content behind a modal dialog is inert and its live regions go unheard, so a message caused inside
 * an open dialog (the Wishlist drawer, the quick add modal) goes to a status region in that dialog;
 * everything else goes to the page region rendered by `snippets/wishlist-drawer.liquid`.
 */
const announcer = {
  /** @type {number | undefined} */
  timer: undefined,

  /**
   * @param {string} message
   * @param {Element | null} [origin] - The element the shopper used.
   */
  announce(message, origin) {
    if (!message) return;

    const region = this.regionFor(origin);
    if (!region) return;

    clearTimeout(this.timer);

    for (const other of document.querySelectorAll('[data-wishlist-status]')) {
      if (other !== region) other.textContent = '';
    }

    // Clearing first lets the same message be announced twice in a row.
    region.textContent = '';
    this.timer = window.setTimeout(() => {
      region.textContent = message;
    }, ANNOUNCE_DELAY);
  },

  /**
   * @param {Element | null} [origin]
   * @returns {Element | null}
   */
  regionFor(origin) {
    const dialog = origin?.closest('dialog[open]');

    if (dialog) {
      let region = dialog.querySelector('[data-wishlist-status]:not([data-wishlist-status="page"])');

      if (!region) {
        region = document.createElement('p');
        region.className = 'visually-hidden';
        region.setAttribute('role', 'status');
        region.setAttribute('data-wishlist-status', '');
        // Outside the dialog's re-rendered content, so it survives a quick add morph.
        dialog.append(region);
      }

      return region;
    }

    return document.querySelector('[data-wishlist-status="page"]');
  },
};

/**
 * Closes the header menu drawer (`header-drawer`) when it is open, and resolves once it has fully
 * closed. A `/#wishlist` menu link is inside that menu: left open, the menu covers the Wishlist drawer
 * at 990px and wider, and when it closes later it removes the page's focus trap, which by then belongs
 * to the Wishlist drawer. When focus was in the menu, it moves to the menu button, which stays
 * visible, so the Wishlist drawer returns focus there when it closes.
 * @returns {Promise<void>}
 */
async function closeHeaderMenu() {
  for (const menu of document.querySelectorAll('header-drawer')) {
    const details = menu.querySelector(':scope > details[open]');
    const close = /** @type {{ close?: unknown }} */ (menu).close;
    if (!(details instanceof HTMLDetailsElement) || typeof close !== 'function') continue;

    const hadFocus = details.contains(document.activeElement);
    const waiting = new AbortController();

    await new Promise((resolve) => {
      // HeaderDrawer#close removes `open` and then the focus trap in one callback; `toggle` is queued
      // after both. The timeout covers a close that never finishes (for example a mid-close re-render).
      details.addEventListener('toggle', resolve, { once: true, signal: waiting.signal });
      setTimeout(resolve, MENU_CLOSE_TIMEOUT);
      close.call(menu);
    });
    waiting.abort();

    if (hadFocus) details.querySelector('summary')?.focus({ preventScroll: true });
  }
}

/**
 * Opens the Wishlist drawer once its element is upgraded, after closing the header menu.
 * @returns {Promise<void>}
 */
async function openDrawer() {
  await customElements.whenDefined('theme-drawer');
  await closeHeaderMenu();

  const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (document.getElementById(DRAWER_ID));
  drawer?.open?.();
}

/**
 * Keeps `aria-expanded` on every trigger of the drawer (the header button) in sync.
 * @param {Event} event
 */
function handleDrawerToggle(event) {
  const drawer = event.target;
  if (!(drawer instanceof HTMLElement) || drawer.id !== DRAWER_ID) return;

  const expanded = String(drawer.hasAttribute('open'));
  for (const trigger of document.querySelectorAll(`[aria-controls="${DRAWER_ID}"]`)) {
    trigger.setAttribute('aria-expanded', expanded);
  }
}

/**
 * Opens the drawer from links to `#wishlist` (for example a menu link to `/#wishlist`), on any page.
 * @param {MouseEvent} event
 */
function handleDrawerLinkClick(event) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
  if (!(link instanceof HTMLAnchorElement) || link.hash !== DRAWER_HASH || link.origin !== location.origin) return;
  if (!document.getElementById(DRAWER_ID)) return;

  event.preventDefault();
  openDrawer();
}

/**
 * Applies the saved state to every toggle and count on the page.
 */
function syncAll() {
  const saved = new Set(Wishlist.getProducts());

  for (const toggle of document.querySelectorAll('wishlist-toggle-component')) {
    if (toggle instanceof WishlistToggleComponent) toggle.sync(saved);
  }

  for (const count of document.querySelectorAll('wishlist-count')) {
    if (count instanceof WishlistCount) count.render(saved.size);
  }
}

/**
 * Section re-renders morph toggles in place and write back the server's unpressed state (morph never
 * calls `updatedCallback` on live elements), and a combined listing swap can change the product.
 * Watching those attributes re-applies the saved state.
 */
const toggleObserver = new MutationObserver((records) => {
  /** @type {Set<WishlistToggleComponent>} */
  const toggles = new Set();

  for (const { target } of records) {
    const toggle = target instanceof Element ? target.closest('wishlist-toggle-component') : null;
    if (toggle instanceof WishlistToggleComponent && toggle.isConnected) toggles.add(toggle);
  }

  if (toggles.size === 0) return;

  const saved = new Set(Wishlist.getProducts());
  for (const toggle of toggles) toggle.sync(saved);
});

/**
 * A heart toggle for one product, on a product card or the product page (`snippets/wishlist-toggle.liquid`).
 *
 * The saved state is applied when the toggle connects (first render, quick add, infinite scroll and
 * other inserted markup), when the wishlist changes anywhere (this page or another tab) and when a
 * section re-render resets its attributes.
 *
 * @typedef {object} ToggleRefs
 * @property {HTMLButtonElement} button - The toggle button; `aria-pressed` reflects the saved state.
 *
 * @extends {Component<ToggleRefs>}
 */
class WishlistToggleComponent extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController | undefined} */
  #connection;

  connectedCallback() {
    super.connectedCallback();

    this.#connection?.abort();
    this.#connection = new AbortController();

    this.refs.button.addEventListener('animationend', this.#handleAnimationEnd, { signal: this.#connection.signal });
    toggleObserver.observe(this, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-product-id', 'aria-pressed'],
    });

    this.sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#connection?.abort();
    this.#connection = undefined;
  }

  /** @returns {string} The product this toggle saves. */
  get productId() {
    return this.dataset.productId ?? '';
  }

  /** @returns {string} The product title, for announcements. */
  get productTitle() {
    return this.dataset.productTitle ?? '';
  }

  /** @returns {string} The product handle, saved with the id so the drawer can load the product. */
  get productHandle() {
    return this.dataset.productHandle ?? '';
  }

  /**
   * Saves or removes the product. Bound with `on:click="/toggle"`.
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();

    const id = this.productId;
    if (!isProductId(id)) return;

    const { button } = this.refs;
    const adding = !Wishlist.has(id);
    const saved = adding ? Wishlist.add(id, this.productHandle) : Wishlist.remove([id]);

    if (!saved) {
      announcer.announce(getStrings().saveError ?? '', button);
      return;
    }

    // The change event already synced every toggle and count on the page.
    this.#animate(adding ? 'add' : 'remove');
    announcer.announce(productString(adding ? 'added' : 'removed', this.productTitle), button);
  }

  /**
   * Applies the saved state through `aria-pressed`; the accessible name stays the same in both states.
   * The attribute is only written when it differs, so the attribute observer settles after one pass.
   * @param {Set<string>} [saved] - The saved ids, when the caller already read them.
   */
  sync(saved = new Set(Wishlist.getProducts())) {
    const { button } = this.refs;
    if (!button) return;

    const pressedValue = String(saved.has(this.productId));

    if (button.getAttribute('aria-pressed') !== pressedValue) {
      button.setAttribute('aria-pressed', pressedValue);
    }
  }

  /**
   * Restarts the heart animation (`wishlist-styles`; reduced motion gets a fade).
   * @param {'add' | 'remove'} kind
   */
  #animate(kind) {
    const { button } = this.refs;

    button.removeAttribute('data-animate');
    // Reading layout restarts the CSS animation when the same kind plays twice in a row.
    void button.offsetWidth;
    button.setAttribute('data-animate', kind);
  }

  #handleAnimationEnd = () => {
    this.refs.button?.removeAttribute('data-animate');
  };
}

/**
 * Shows the number of saved products: as a number (header bubble, drawer badge; hidden at zero) or,
 * with `data-format="label"`, as screen reader text such as "3 saved items".
 */
class WishlistCount extends HTMLElement {
  /** @type {number | null} */
  #count = null;

  connectedCallback() {
    this.render();
  }

  /**
   * @param {number} [count]
   */
  render(count = Wishlist.getProducts().length) {
    const isLabel = this.dataset.format === 'label';
    const text = isLabel ? countString(count) : count > 0 ? String(count) : '';

    if (this.textContent !== text) this.textContent = text;
    if (!isLabel) this.hidden = count === 0;

    if (!isLabel && this.#count !== null && this.#count !== count && count > 0) {
      this.removeAttribute('data-animate');
      void this.offsetWidth;
      this.setAttribute('data-animate', '');
    }

    this.#count = count;
  }
}

/**
 * The Wishlist drawer content (`snippets/wishlist-drawer.liquid`).
 *
 * When the drawer opens, the saved ids are rendered as rows through the Section Rendering API on the
 * search route (`sections/wishlist-results.liquid`, up to {@link REQUEST_BATCH_SIZE} ids per request),
 * in the saved order. Search leaves out some products that are still for sale (Unlisted, or hidden
 * from search), so those are loaded from their product URL when their handle is known.
 *
 * A saved product that still can't be loaded (deleted or unpublished, not sold in the shopper's
 * market, not indexed yet) stays saved and is counted in a note under the rows, so the rows and the
 * note add up to the count. Only after it has failed to load for {@link MISS_GRACE_MS} is it dropped.
 * A failed request keeps every id and shows a retry. Removing a row keeps focus in the list; changes
 * from other tabs update the open drawer.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLElement} body - The scrolling area, busy while rows load.
 * @property {HTMLUListElement} list - The rows.
 * @property {HTMLElement} empty - Shown when nothing is saved.
 * @property {HTMLElement} emptyHeading - Receives focus when the last row is removed.
 * @property {HTMLElement} error - Shown when the rows could not be loaded.
 * @property {HTMLElement} [errorMessage]
 * @property {HTMLElement} [unavailable] - Counts saved products that can't be shown right now; receives
 *   focus when the last row is removed and only those remain.
 * @property {HTMLElement} [note] - "Saved on this device", shown with rows.
 *
 * @extends {Component<DrawerRefs>}
 */
class WishlistDrawerComponent extends Component {
  requiredRefs = ['body', 'list', 'empty', 'emptyHeading', 'error'];

  /** @type {AbortController | undefined} */
  #connection;

  /** @type {AbortController | undefined} */
  #request;

  /**
   * The saved ids the drawer shows (as rows, or counted in the unavailable note), comma separated;
   * `''` for the empty state and `null` when nothing is rendered.
   * @type {string | null}
   */
  #renderedKey = null;

  /** The ids being loaded. @type {string | null} */
  #pendingKey = null;

  /** Saved products that the last load couldn't show. @type {Set<string>} */
  #hiddenIds = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#connection?.abort();
    this.#connection = new AbortController();
    const { signal } = this.#connection;

    document.addEventListener(CHANGE_EVENT, this.#handleChange, { signal });
    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleOpen, { signal });

    // The drawer can open before this module loads.
    if (this.#isOpen) this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#connection?.abort();
    this.#connection = undefined;
    this.#cancelRequest();
  }

  /** @returns {HTMLElement | null} */
  get #drawer() {
    return this.closest('theme-drawer');
  }

  /** @returns {boolean} */
  get #isOpen() {
    return Boolean(this.#drawer?.hasAttribute('open'));
  }

  /** @returns {HTMLElement[]} The rendered product rows. */
  get #rows() {
    return /** @type {HTMLElement[]} */ (Array.from(this.refs.list.querySelectorAll(':scope > [data-wishlist-item]')));
  }

  /**
   * Loads the rows again after a failed request. Bound with `on:click="/retry"`.
   */
  retry() {
    this.#load({ force: true });
  }

  /**
   * Removes a row's product. Bound with `on:click="/removeItem"` on the rows' remove buttons.
   * @param {Event} event
   */
  removeItem(event) {
    const button = event.target instanceof Element ? event.target : null;
    const row = button?.closest('[data-wishlist-item]');
    if (!(row instanceof HTMLElement)) return;

    const id = row.dataset.productId ?? '';
    const title = row.dataset.productTitle ?? '';

    // The drawer closes on clicks whose target is no longer inside it, so the row (with the clicked
    // button) is removed after this click finishes. The change event then removes the row and moves
    // focus to a neighbor (#refresh).
    setTimeout(() => {
      if (!Wishlist.has(id)) return;

      // Announced from the drawer itself: the button may be gone, and the page behind a modal drawer is inert.
      if (!Wishlist.remove([id])) {
        announcer.announce(getStrings().saveError ?? '', this);
        return;
      }

      announcer.announce(productString('removed', title), this);
    });
  }

  /** @param {Event} event */
  #handleOpen = (event) => {
    if (event.target !== this.#drawer) return;
    this.#refresh();
  };

  /**
   * Keeps an open drawer current. While it is closed, the next open refreshes it.
   */
  #handleChange = () => {
    if (this.#isOpen) this.#refresh();
  };

  /**
   * Brings the rows in line with the saved ids: removals are applied in place (focus stays in the
   * list and nothing is fetched); anything else loads the rows.
   */
  #refresh() {
    const ids = Wishlist.getProducts();

    if (ids.length === 0) {
      this.#cancelRequest();
      this.#renderEmpty();
      return;
    }

    if (this.#renderedKey !== null && this.#rows.length > 0) {
      const shown = ids.filter((id) => !this.#hiddenIds.has(id));
      const kept = this.#rows.map((row) => row.dataset.productId ?? '').filter((id) => shown.includes(id));

      if (kept.length === shown.length && kept.every((id, index) => id === shown[index])) {
        this.#hiddenIds = new Set(ids.filter((id) => this.#hiddenIds.has(id)));
        this.#renderUnavailable();

        for (const row of this.#rows) {
          if (!shown.includes(row.dataset.productId ?? '')) this.#removeRow(row);
        }

        if (this.#rows.length === 0) this.#renderNoRows();
        this.#renderedKey = ids.join(',');
        return;
      }
    }

    this.#load();
  }

  /**
   * Fetches and renders the rows for the saved ids.
   * @param {{ force?: boolean }} [options]
   */
  async #load({ force = false } = {}) {
    const ids = Wishlist.getProducts();
    const key = ids.join(',');

    if (ids.length === 0) {
      this.#cancelRequest();
      this.#renderEmpty();
      return;
    }

    if (!force && (key === this.#renderedKey || key === this.#pendingKey)) return;

    this.#cancelRequest();
    const request = new AbortController();
    this.#request = request;
    this.#pendingKey = key;

    // Rows already showing stay in place while they refresh, and so does a state that holds focus
    // (the error's retry button, the empty state's link) until the rows can take focus.
    if (!this.#renderedKey && !this.#stateHasFocus) this.#renderSkeleton(Math.min(ids.length, MAX_SKELETON_ROWS));
    this.refs.body.setAttribute('aria-busy', 'true');

    try {
      /** @type {Map<string, HTMLElement>} */
      const rows = new Map();
      /** @type {string[]} */
      let missing = [];

      const batches = [];
      for (let index = 0; index < ids.length; index += REQUEST_BATCH_SIZE) {
        batches.push(ids.slice(index, index + REQUEST_BATCH_SIZE));
      }

      const responses = await Promise.all(batches.map((batch) => this.#fetchRows(batch, request.signal)));
      if (request.signal.aborted) return;

      responses.forEach((results, index) => {
        const batch = batches[index] ?? [];
        /** @type {Set<string>} */
        const found = new Set();

        for (const row of results.querySelectorAll(':scope > [data-wishlist-item]')) {
          const id = row instanceof HTMLElement ? row.dataset.productId ?? '' : '';
          if (!batch.includes(id) || found.has(id)) continue;

          found.add(id);
          rows.set(id, /** @type {HTMLElement} */ (row));
        }

        // #fetchRows only resolves responses from a search that ran, so search left these ids out.
        missing.push(...batch.filter((id) => !found.has(id)));
      });

      if (missing.length > 0) {
        const recovered = await this.#fetchProductRows(missing, request.signal);
        if (request.signal.aborted) return;

        for (const [id, row] of recovered) rows.set(id, row);
        missing = missing.filter((id) => !rows.has(id));
      }

      // Handles from loaded rows let products that later drop out of search load from their product URL.
      /** @type {Map<string, string>} */
      const handles = new Map();
      for (const [id, row] of rows) handles.set(id, row.dataset.productHandle ?? '');
      Wishlist.rememberHandles(handles);

      // One miss doesn't prove a product is gone; only products that have kept failing are dropped.
      const expired = Wishlist.noteMisses(missing, [...rows.keys()]);

      // The list may have changed while the request was in flight; render what is still saved.
      const current = Wishlist.getProducts().filter((id) => !expired.includes(id));
      const visible = current.filter((id) => rows.has(id));
      this.#hiddenIds = new Set(current.filter((id) => missing.includes(id)));

      this.#renderRows(visible.map((id) => /** @type {HTMLElement} */ (rows.get(id))));
      this.#renderedKey = current.filter((id) => rows.has(id) || this.#hiddenIds.has(id)).join(',');

      if (expired.length > 0) Wishlist.remove(expired, 'cleanup');

      // Products saved while the request was in flight still need rows.
      if (Wishlist.getProducts().some((id) => !ids.includes(id))) this.#load();
    } catch (error) {
      if (request.signal.aborted) return;

      console.warn('[wishlist] Could not load the saved products:', error);
      this.#renderError();
    } finally {
      if (this.#request === request) {
        this.#request = undefined;
        this.#pendingKey = null;
        this.refs.body.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * @param {string[]} ids
   * @param {AbortSignal} signal
   * @returns {Promise<Element>} The response's results list.
   */
  async #fetchRows(ids, signal) {
    const url = new URL(this.dataset.searchUrl || Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    // Sold out products stay listed even when search is set to hide them.
    url.searchParams.set('options[unavailable_products]', 'show');

    const html = await sectionRenderer.getSectionHTML(RESULTS_SECTION_ID, false, url, signal);
    const results = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-wishlist-results]');

    if (!results || results.getAttribute('data-search-performed') !== 'true') {
      throw new Error('The wishlist response has no search results');
    }

    return results;
  }

  /**
   * Loads rows for saved products that search left out, from their product URLs, where Unlisted
   * products and products hidden from search are still for sale. Products without a known handle,
   * and any failed request, stay missing.
   * @param {string[]} ids
   * @param {AbortSignal} signal
   * @returns {Promise<Map<string, HTMLElement>>} The rows found, by product id.
   */
  async #fetchProductRows(ids, signal) {
    const handles = Wishlist.getHandles();
    const root = (this.dataset.rootUrl || '/').replace(/\/+$/, '');
    /** @type {Map<string, HTMLElement>} */
    const rows = new Map();

    const requests = ids
      .filter((id) => handles.has(id))
      .slice(0, REQUEST_BATCH_SIZE)
      .map(async (id) => {
        const url = new URL(`${root}/products/${encodeURIComponent(handles.get(id) ?? '')}`, location.origin);
        url.searchParams.set('section_id', RESULTS_SECTION_ID);

        try {
          // A plain fetch rather than getSectionHTML, which ignores the HTTP status.
          const response = await fetch(url, { signal });
          if (!response.ok) return;

          const html = await response.text();
          const row = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(`[data-wishlist-results] > [data-wishlist-item][data-product-id="${id}"]`);

          if (row instanceof HTMLElement) rows.set(id, row);
        } catch {
          // Stays missing; the next load tries again.
        }
      });

    await Promise.all(requests);
    return rows;
  }

  #cancelRequest() {
    this.#request?.abort();
    this.#request = undefined;
    this.#pendingKey = null;
    this.refs.body?.removeAttribute('aria-busy');
  }

  /**
   * @param {HTMLElement[]} rows
   */
  #renderRows(rows) {
    if (rows.length === 0) {
      this.#renderNoRows();
      return;
    }

    const { list, empty, error, note } = this.refs;
    const focusTarget = this.#focusReplacement();
    const stateHadFocus = this.#stateHasFocus;

    // Fresh nodes rather than a morph, so a row never turns into another product's row.
    list.replaceChildren(...rows);
    list.hidden = false;
    empty.hidden = true;
    error.hidden = true;
    if (note) note.hidden = false;
    this.#renderUnavailable();

    if (focusTarget) {
      focusTarget();
    } else if (stateHadFocus) {
      /** @type {HTMLElement | null} */ (list.querySelector('.wishlist-item__title'))?.focus();
    }
  }

  /**
   * Shows what is left when no row can be shown: the unavailable note while saved products can't be
   * shown right now, otherwise the empty state.
   * @param {boolean} [moveFocus] - Whether focus must move here (the focused row was just removed).
   */
  #renderNoRows(moveFocus = false) {
    const { list, empty, emptyHeading, error, note, unavailable } = this.refs;

    if (this.#hiddenIds.size === 0 || !unavailable) {
      this.#renderEmpty();
      if (moveFocus) emptyHeading.focus();
      return;
    }

    const active = document.activeElement;
    const hadFocus = moveFocus || [list, empty, error].some((element) => element.contains(active));

    list.replaceChildren();
    list.hidden = true;
    empty.hidden = true;
    error.hidden = true;
    if (note) note.hidden = false;
    this.#renderUnavailable();

    if (hadFocus) unavailable.focus();
  }

  /**
   * Updates the note that counts saved products the drawer can't show right now.
   */
  #renderUnavailable() {
    const { unavailable } = this.refs;
    if (!unavailable) return;

    const { unavailableOne, unavailableOther } = getStrings();
    const count = this.#hiddenIds.size;
    const text = count > 0 ? pluralString(count, unavailableOne, unavailableOther) : '';

    if (unavailable.textContent !== text) unavailable.textContent = text;
    unavailable.hidden = text === '';
  }

  /**
   * @returns {boolean} Whether focus is in the empty, error or unavailable state, which rows are about
   *   to replace.
   */
  get #stateHasFocus() {
    const { empty, error, unavailable } = this.refs;
    const active = document.activeElement;
    if (!active) return false;

    return (
      (!empty.hidden && empty.contains(active)) ||
      (!error.hidden && error.contains(active)) ||
      (Boolean(unavailable) && unavailable === active)
    );
  }

  /**
   * @param {number} count
   */
  #renderSkeleton(count) {
    const { list, empty, error, note, unavailable } = this.refs;

    const rows = Array.from({ length: count }, () => {
      const row = document.createElement('li');
      row.className = 'wishlist-item wishlist-item--skeleton';
      row.setAttribute('aria-hidden', 'true');
      row.innerHTML =
        '<span class="wishlist-item__media"></span><span class="wishlist-item__details"><span class="wishlist-item__skeleton-line"></span><span class="wishlist-item__skeleton-line"></span></span>';
      return row;
    });

    list.replaceChildren(...rows);
    list.hidden = false;
    empty.hidden = true;
    error.hidden = true;
    if (note) note.hidden = true;
    if (unavailable) unavailable.hidden = true;
  }

  #renderEmpty() {
    const { list, empty, emptyHeading, error, note, unavailable } = this.refs;
    const active = document.activeElement;
    const hadFocus = list.contains(active) || error.contains(active) || (Boolean(unavailable) && unavailable === active);

    list.replaceChildren();
    list.hidden = true;
    error.hidden = true;
    empty.hidden = false;
    if (note) note.hidden = true;
    this.#hiddenIds = new Set();
    this.#renderUnavailable();
    this.#renderedKey = '';

    if (hadFocus) emptyHeading.focus();
  }

  #renderError() {
    const { list, empty, error, errorMessage, note, unavailable } = this.refs;
    const active = document.activeElement;
    const hadFocus =
      list.contains(active) || (!empty.hidden && empty.contains(active)) || (Boolean(unavailable) && unavailable === active);

    list.replaceChildren();
    list.hidden = true;
    empty.hidden = true;
    error.hidden = false;
    if (note) note.hidden = true;
    this.#hiddenIds = new Set();
    this.#renderUnavailable();
    this.#renderedKey = null;

    if (hadFocus) errorMessage?.focus();
  }

  /**
   * Removes a row; when it holds focus, focus moves to the next row, the previous row, the
   * unavailable note or the empty state.
   * @param {HTMLElement} row
   */
  #removeRow(row) {
    if (!row.contains(document.activeElement)) {
      row.remove();
      return;
    }

    const neighbor = /** @type {HTMLElement | null} */ (
      row.nextElementSibling?.matches('[data-wishlist-item]')
        ? row.nextElementSibling
        : row.previousElementSibling?.matches('[data-wishlist-item]')
          ? row.previousElementSibling
          : null
    );

    row.remove();

    if (neighbor) {
      /** @type {HTMLElement | null} */ (neighbor.querySelector('.wishlist-item__remove'))?.focus();
    } else {
      this.#renderNoRows(true);
    }
  }

  /**
   * When focus is inside rows about to be replaced, returns a callback that restores it on the new
   * row of the same product (or the list's first remove button).
   * @returns {(() => void) | null}
   */
  #focusReplacement() {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.refs.list.contains(active)) return null;

    const productId = active.closest('[data-wishlist-item]')?.getAttribute('data-product-id');
    const isRemoveButton = active.classList.contains('wishlist-item__remove');

    return () => {
      const row = this.#rows.find((candidate) => candidate.dataset.productId === productId) ?? this.#rows[0];
      const target = row?.querySelector(isRemoveButton ? '.wishlist-item__remove' : '.wishlist-item__title');
      if (target instanceof HTMLElement) target.focus();
    };
  }
}

if (!customElements.get('wishlist-toggle-component')) {
  customElements.define('wishlist-toggle-component', WishlistToggleComponent);
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}

// Page-level wiring; a module runs once per document, so these listeners are registered once.
document.documentElement.dataset.wishlist = Wishlist.isAvailable ? 'ready' : 'unavailable';

document.addEventListener(CHANGE_EVENT, syncAll);
document.addEventListener(DrawerOpenEvent.eventName, handleDrawerToggle);
document.addEventListener(DrawerCloseEvent.eventName, handleDrawerToggle);
document.addEventListener('click', handleDrawerLinkClick);

window.addEventListener('storage', (event) => {
  if (event.key === null || event.key === STORAGE_KEY) Wishlist.sync();
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) Wishlist.sync();
});

/**
 * Opens the drawer when the page is reached through `#wishlist`, then drops the hash so a reload or a
 * shared link doesn't reopen it.
 */
function openFromHash() {
  if (location.hash !== DRAWER_HASH || !document.getElementById(DRAWER_ID)) return;

  history.replaceState(history.state, '', `${location.pathname}${location.search}`);
  openDrawer();
}

window.addEventListener('hashchange', openFromHash);
openFromHash();
